<template>
  <!-- [sc-48351 / task 3] La sidebar di CRM PLUS, da crmplus-web: scura,
       comprimibile, cassetto su mobile. Le voci sono quelle della home di
       START, con gli stessi permessi; in fondo le pagine che si aprono di
       rado, l'aiuto e l'uscita, che in START stanno nella barra in alto. -->
  <aside
    class="plus-sidebar"
    :class="{
      'plus-sidebar--collapsed': collapsed,
      'plus-sidebar--mobile-open': mobileOpen,
    }"
    aria-label="Menu del gestionale"
  >
    <div class="plus-sidebar__header">
      <router-link to="/home" class="plus-sidebar__brand" title="Home">
        <img
          v-if="showCompanyLogo"
          class="plus-sidebar__logo"
          :src="companyLogoSrc"
          :alt="companyName || 'Logo aziendale'"
          @error="logoUnavailable = true"
        />
        <img
          v-else
          class="plus-sidebar__logo"
          src="@/assets/logo-mark.png"
          alt=""
        />
        <span class="plus-sidebar__name">{{ companyName || "CRM" }}</span>
      </router-link>
    </div>

    <button
      type="button"
      class="plus-sidebar__toggle"
      :title="collapsed ? 'Espandi menu' : 'Comprimi menu'"
      :aria-label="collapsed ? 'Espandi menu' : 'Comprimi menu'"
      @click="$emit('toggle')"
    >
      <i
        class="fa-solid"
        :class="collapsed ? 'fa-chevron-right' : 'fa-chevron-left'"
        aria-hidden="true"
      ></i>
    </button>

    <nav class="plus-sidebar__nav" aria-label="Navigazione principale">
      <template v-for="voce in voci" :key="voce.to">
        <!-- Listini: sottomenu con una voce per categoria, come le schede
             della pagina. -->
        <div
          v-if="voce.categorie"
          class="plus-sidebar__group"
          :class="{ 'plus-sidebar__group--open': listiniAperto && !collapsed }"
        >
          <button
            type="button"
            class="plus-sidebar__item"
            :class="{ 'plus-sidebar__item--active': attiva(voce) }"
            :aria-expanded="String(listiniAperto && !collapsed)"
            :title="collapsed ? voce.etichetta : undefined"
            @click="toggleListini"
          >
            <i
              class="fa-solid plus-sidebar__icon"
              :class="voce.icona"
              aria-hidden="true"
            ></i>
            <span class="plus-sidebar__label">{{ voce.etichetta }}</span>
            <i
              class="fa-solid fa-chevron-down plus-sidebar__chevron"
              aria-hidden="true"
            ></i>
          </button>
          <div class="plus-sidebar__submenu">
            <div class="plus-sidebar__submenu-inner">
              <router-link
                v-for="sotto in vociListini"
                :key="sotto.to"
                :to="sotto.to"
                class="plus-sidebar__subitem"
                :class="{ 'plus-sidebar__subitem--active': attiva(sotto) }"
              >
                <i :class="sotto.icona" aria-hidden="true"></i>
                {{ sotto.etichetta }}
              </router-link>
            </div>
          </div>
        </div>
        <router-link
          v-else
          :to="voce.to"
          class="plus-sidebar__item"
          :class="{ 'plus-sidebar__item--active': attiva(voce) }"
          :title="collapsed ? voce.etichetta : undefined"
        >
          <i
            class="fa-solid plus-sidebar__icon"
            :class="voce.icona"
            aria-hidden="true"
          ></i>
          <span class="plus-sidebar__label">{{ voce.etichetta }}</span>
        </router-link>
      </template>
    </nav>

    <div class="plus-sidebar__footer">
      <!-- Impostazioni: le pagine che si aprono di rado, raggruppate come
           nella barra di START. Solo per gli amministratori. -->
      <div
        v-if="impostazioni.length"
        class="plus-sidebar__group"
        :class="{
          'plus-sidebar__group--open': impostazioniAperto && !collapsed,
        }"
      >
        <button
          type="button"
          class="plus-sidebar__item"
          :class="{ 'plus-sidebar__item--active': inImpostazioni }"
          :aria-expanded="String(impostazioniAperto && !collapsed)"
          :title="collapsed ? 'Impostazioni' : undefined"
          @click="toggleImpostazioni"
        >
          <i class="fa-solid fa-gear plus-sidebar__icon" aria-hidden="true"></i>
          <span class="plus-sidebar__label">Impostazioni</span>
          <i
            class="fa-solid fa-chevron-down plus-sidebar__chevron"
            aria-hidden="true"
          ></i>
        </button>
        <div class="plus-sidebar__submenu">
          <div class="plus-sidebar__submenu-inner">
            <router-link
              v-for="voce in impostazioni"
              :key="voce.to"
              :to="voce.to"
              class="plus-sidebar__subitem"
              :class="{ 'plus-sidebar__subitem--active': attiva(voce) }"
            >
              <i class="fa-solid" :class="voce.icona" aria-hidden="true"></i>
              {{ voce.etichetta }}
            </router-link>
          </div>
        </div>
      </div>

      <!-- Aiuto: manuale e giro guidato, come nella barra di START. -->
      <div
        class="plus-sidebar__group"
        :class="{ 'plus-sidebar__group--open': aiutoAperto && !collapsed }"
      >
        <button
          type="button"
          class="plus-sidebar__item"
          :aria-expanded="String(aiutoAperto && !collapsed)"
          :title="collapsed ? 'Aiuto' : undefined"
          @click="toggleAiuto"
        >
          <i
            class="fa-solid fa-circle-question plus-sidebar__icon"
            aria-hidden="true"
          ></i>
          <span class="plus-sidebar__label">Aiuto</span>
          <i
            class="fa-solid fa-chevron-down plus-sidebar__chevron"
            aria-hidden="true"
          ></i>
        </button>
        <div class="plus-sidebar__submenu">
          <div class="plus-sidebar__submenu-inner">
            <button
              type="button"
              class="plus-sidebar__subitem"
              @click="openGuide"
            >
              <i class="fa-solid fa-book-open" aria-hidden="true"></i>
              Manuale operativo
            </button>
            <button
              v-if="paginaSpiegabile"
              type="button"
              class="plus-sidebar__subitem"
              @click="spiegaPagina"
            >
              <i class="fa-solid fa-lightbulb" aria-hidden="true"></i>
              Spiegami questa pagina
            </button>
            <button
              type="button"
              class="plus-sidebar__subitem"
              @click="rivediTutorial"
            >
              <i class="fa-solid fa-graduation-cap" aria-hidden="true"></i>
              Rivedi il tutorial
            </button>
          </div>
        </div>
      </div>

      <button
        v-if="!$route.meta.hideLogout"
        type="button"
        class="plus-sidebar__item plus-sidebar__item--logout"
        :disabled="isLoggingOut"
        :title="collapsed ? 'Esci' : undefined"
        @click="logout"
      >
        <i
          class="plus-sidebar__icon"
          :class="
            isLoggingOut
              ? 'fa-solid fa-spinner fa-spin'
              : 'fa-solid fa-right-from-bracket'
          "
          aria-hidden="true"
        ></i>
        <span class="plus-sidebar__label">{{
          isLoggingOut ? "Uscita…" : "Esci"
        }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
import { setAuth } from "@/router";
import { trackEvent, EVENTS } from "@/utilities/analytics";
import { companyLogoUrl } from "@/config/branding";
import { haSpiegazione } from "@/utilities/onboarding";
import {
  vociImpostazioni,
  vociListini,
  vociSidebar,
} from "@/utilities/layoutPlus";

export default {
  name: "PlusSidebar",
  props: {
    collapsed: { type: Boolean, default: false },
    mobileOpen: { type: Boolean, default: false },
  },
  // `close-mobile`: le azioni che non cambiano pagina devono chiudere il
  // cassetto da sole, altrimenti su mobile resta sopra il tutorial.
  emits: ["toggle", "close-mobile"],
  data() {
    return {
      aiutoAperto: false,
      // Aperto se si entra già dentro un listino.
      listiniAperto: this.$route?.path?.startsWith("/home/listini") ?? false,
      impostazioniAperto: false,
      isLoggingOut: false,
      logoUnavailable: false,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.currentUser?.isAdmin ?? false;
    },
    voci() {
      return vociSidebar({
        isAdmin: this.isAdmin,
        can: this.$store.getters["auth/can"],
        features: this.$store.getters["company/features"] || {},
      });
    },
    vociListini() {
      const branding = this.$store.getters["company/branding"];
      return vociListini(
        branding?.productCategories,
        branding?.itemCategoriesInUse,
        {
          isAdmin: this.isAdmin,
          features: this.$store.getters["company/features"] || {},
        }
      );
    },
    impostazioni() {
      return vociImpostazioni({
        isAdmin: this.isAdmin,
        features: this.$store.getters["company/features"] || {},
      });
    },
    /** Si è dentro una delle pagine di Impostazioni. */
    inImpostazioni() {
      return this.impostazioni.some((voce) => this.attiva(voce));
    },
    paginaSpiegabile() {
      return haSpiegazione(this.$route.name);
    },
    branding() {
      return this.$store.getters["company/branding"];
    },
    companyName() {
      return this.branding?.name || "";
    },
    showCompanyLogo() {
      return Boolean(this.branding?.hasLogo) && !this.logoUnavailable;
    },
    companyLogoSrc() {
      return companyLogoUrl(undefined, this.branding?.logoVersion);
    },
  },
  created() {
    // Entrando già su Marchio o Dati, il gruppo è aperto.
    this.impostazioniAperto = this.inImpostazioni;
  },
  watch: {
    "$route.path"(path) {
      this.aiutoAperto = false;
      // Uscendo dai listini il sottomenu si richiude; entrandoci si apre.
      this.listiniAperto = path.startsWith("/home/listini");
      this.impostazioniAperto = this.inImpostazioni;
    },
    collapsed(value) {
      if (value) {
        this.aiutoAperto = false;
        this.listiniAperto = false;
        this.impostazioniAperto = false;
      }
    },
  },
  methods: {
    /** Home solo esatta: altrimenti sarebbe accesa su ogni pagina. */
    attiva(voce) {
      const path = this.$route.path;
      if (voce.esatta) return path === voce.to;
      return path === voce.to || path.startsWith(`${voce.to}/`);
    },

    toggleListini() {
      if (this.collapsed) {
        this.$emit("toggle");
        this.listiniAperto = true;
        return;
      }
      this.listiniAperto = !this.listiniAperto;
    },

    toggleImpostazioni() {
      if (this.collapsed) {
        this.$emit("toggle");
        this.impostazioniAperto = true;
        return;
      }
      this.impostazioniAperto = !this.impostazioniAperto;
    },

    toggleAiuto() {
      // Da compressa il sottomenu non si vedrebbe: prima si riapre.
      if (this.collapsed) {
        this.$emit("toggle");
        this.aiutoAperto = true;
        return;
      }
      this.aiutoAperto = !this.aiutoAperto;
    },

    openGuide() {
      trackEvent(EVENTS.FEATURE_USED, { feature: "guide", action: "open" });
      this.aiutoAperto = false;
      this.$emit("close-mobile");
      const locale = this.$i18n?.locale === "en" ? "en" : "it";
      const file = locale === "en" ? "manuale_en.html" : "manuale.html";
      const base = process.env.BASE_URL || "/";
      window.open(`${base}guide/${file}`, "_blank", "noopener");
    },

    spiegaPagina() {
      this.aiutoAperto = false;
      this.$emit("close-mobile");
      return this.$store.dispatch("spiegaQuestaPagina", this.$route.name);
    },

    rivediTutorial() {
      this.aiutoAperto = false;
      this.$emit("close-mobile");
      this.$store.dispatch("riapriTutorial");
      return this.$router.push({ name: "home" });
    },

    /** Come il logout della barra di START, marchio compreso. */
    async logout() {
      this.$emit("close-mobile");
      this.isLoggingOut = true;
      try {
        const response = await this.$axios.post("/auth/logout");
        if (response.status === 200) {
          setAuth(false);
          await this.$store.dispatch("fetchCompanyBranding");
          await this.$router.push("/login");
          this.$toast.success("Logout effettuato con successo!");
        }
      } catch (error) {
        this.$toast.error("Errore durante il logout");
      } finally {
        this.isLoggingOut = false;
      }
    },
  },
};
</script>
